import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Mic, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLexiaVoice } from "@/contexts/LexiaVoiceContext";
import LexiaAvatar from "@/components/LexiaAvatar";

/**
 * Dashboard card that either invites the learner to a 2-min vocal session with
 * Lexia, or shows a summary once done.
 */
export default function DailyVoiceCard() {
    const [status, setStatus] = useState(null); // {done, first_name, target_words, session, has_target}
    const [loading, setLoading] = useState(true);
    const { openInMode } = useLexiaVoice();

    async function load() {
        try {
            const { data } = await api.get("/daily-voice/today");
            setStatus(data);
        } catch (err) {
            // Non-blocking — a dashboard card that fails silently is fine.
            console.warn("daily-voice/today failed", err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        const onDone = () => load();
        window.addEventListener("lexia:daily-voice-done", onDone);
        return () => window.removeEventListener("lexia:daily-voice-done", onDone);
    }, []);

    if (loading) {
        return (
            <section
                className="mb-6 bg-white border border-lexiBorder rounded-2xl p-6 h-24 animate-pulse"
                data-testid="daily-voice-card-loading"
            />
        );
    }
    if (!status) return null;

    const { done, first_name, target_words = [], session, has_target } = status;
    const firstName = first_name || "";

    if (done && session) {
        const practicedCount = (session.practiced_words || []).length;
        const minutes = Math.max(1, Math.round((session.duration_s || 0) / 60));
        return (
            <section
                className="mb-6 bg-emerald/15 border-l-4 border-emerald border-y-0 border-r border-r-emerald/25 text-ink rounded-2xl p-6 md:p-7 flex flex-wrap items-center justify-between gap-5"
                data-testid="daily-voice-card-done"
            >
                <div className="flex items-start gap-4 min-w-0 flex-1">
                    <LexiaAvatar size="md" state="static" className="shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[11px] tracking-[0.24em] uppercase text-emerald">
                            Question du jour · Lexia
                        </p>
                        <h3 className="font-serif text-xl md:text-2xl leading-tight mt-1 inline-flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald" strokeWidth={2} />
                            Fait aujourd&apos;hui — bravo{firstName ? `, ${firstName}` : ""}&nbsp;!
                        </h3>
                        <p className="text-sm text-inkSoft mt-1">
                            {practicedCount > 0
                                ? `${practicedCount} mot${practicedCount > 1 ? "s" : ""} pratiqué${practicedCount > 1 ? "s" : ""} · ${minutes} min avec Lexia. À demain pour la suite.`
                                : `Session de ${minutes} min avec Lexia terminée.`}
                        </p>
                        {session.summary_fr && (
                            <p className="text-xs text-inkSoft italic mt-2" data-testid="daily-voice-summary">
                                « {session.summary_fr} »
                            </p>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => openInMode("general")}
                    data-testid="daily-voice-open-general"
                    className="inline-flex items-center gap-2 bg-white border border-emerald/40 hover:bg-paperAlt text-emerald rounded-full px-4 py-2 text-sm"
                >
                    <Mic className="w-4 h-4" /> Encore parler
                </button>
            </section>
        );
    }

    const targetPreview = target_words.slice(0, 4).map((w) => w.word).filter(Boolean);
    return (
        <section
            className="mb-6 bg-ink text-white rounded-2xl p-6 md:p-7 flex flex-wrap items-center justify-between gap-5"
            data-testid="daily-voice-card"
        >
            <div className="flex items-start gap-4 min-w-0 flex-1">
                <LexiaAvatar
                    size="lg"
                    state="static"
                    className="shrink-0 ring-offset-ink"
                />
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                        <p className="text-[11px] tracking-[0.24em] uppercase text-white/70">
                            Question du jour · Lexia
                        </p>
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl leading-tight">
                        Prêt{firstName?.endsWith("e") ? "e" : ""} à parler 2 minutes avec moi{firstName ? `, ${firstName}` : ""}&nbsp;?
                    </h3>
                    <p className="text-sm text-white/85 mt-1">
                        {has_target && targetPreview.length > 0 ? (
                            <>
                                On revoit à l&apos;oral : {" "}
                                <span className="italic text-white">
                                    {targetPreview.join(", ")}
                                    {target_words.length > targetPreview.length ? "…" : ""}
                                </span>
                            </>
                        ) : (
                            <>Une mini-conversation libre, adaptée à ton niveau.</>
                        )}
                    </p>
                    <p className="text-[11px] text-white/60 mt-2 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> ~ 2 min · corrections bienveillantes
                    </p>
                </div>
            </div>
            <button
                onClick={() => {
                    openInMode("daily_voice", { target_words });
                    toast.info("Prépare le micro — Lexia arrive dans un instant.");
                }}
                data-testid="daily-voice-start"
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
            >
                <Mic className="w-4 h-4" /> Commencer la session
            </button>
        </section>
    );
}

/**
 * Broadcast helper — the widget calls this when the daily session completes so
 * every open card can re-fetch its status.
 */
export function announceDailyVoiceDone() {
    try {
        window.dispatchEvent(new CustomEvent("lexia:daily-voice-done"));
    } catch {
        // noop
    }
}
